/**
 * Scrubber — the chapter timeline, and one of the three explicit seeks LAW 12 permits.
 *
 * design.md §5.8: *"a chapter scrubber that also carries the co-read member ticks"*.
 * design.md §5.5: *"Seeking is explicit — tap a sentence, 'Play from here', or the scrubber."*
 *
 * ── Chapter-scoped, and that is not a cosmetic choice ───────────────────────
 * §5.7: *"`setPositionState` must be chapter-scoped or the lock screen shows a 90-second track that
 * restarts every page with a useless scrubber."* A page artifact is ~90–110 s of audio, so a
 * page-scoped scrubber is a control that reaches the end and resets four times a chapter — and the
 * lock screen would disagree with the app about what "the track" is. One axis, chapter, in both
 * places. `chapterOffsetSec + el.currentTime` against `chapterDurationSec`, as the design writes it.
 *
 * ── Committed on release, not on every move ─────────────────────────────────
 * A drag emits ~60 pointermove events a second. Writing `currentTime` on each of them against a
 * ranged HTTP source makes Safari cancel and reissue a request per frame; the audible result is a
 * stutter that lasts as long as the thumb is down and a few seconds of silence after it comes up.
 * So the thumb moves live and the seek fires once, on release. The preview position is local state
 * and the incoming `positionMs` is ignored while dragging — otherwise the thumb fights the clock.
 *
 * ── Styling ────────────────────────────────────────────────────────────────
 * Inline, with tokens, for the same reason as `PlaybackBar`: this file owns no stylesheet, and
 * `reader-page.css` belongs to the frame and to another session's diff.
 */

import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
} from "react";

/**
 * P5. A room member's place on this chapter's axis.
 *
 * §7 pins a room to a `book_timeline` version, so every member's position is expressible in the same
 * milliseconds this component already draws. `color` is a `--presence-N` token, assigned by the room
 * and not by this file: a member's colour has to be the same in the scrubber, the member list and
 * the margin, and a component that picked its own would be the second answer.
 *
 * **Nothing passes this in P2** — rooms are P5 — but it is a rendered prop rather than a comment,
 * because the alternative is discovering in P5 that the scrubber has no room for ticks.
 */
export interface ScrubberTick {
  id: string;
  /** Milliseconds into the chapter. */
  ms: number;
  /** A CSS colour, normally `var(--presence-3)`. */
  color: string;
  /** For the accessible description. A person's name. */
  label: string;
}

export interface ScrubberProps {
  /** Milliseconds into the CHAPTER: `chapterOffsetMs + el.currentTime * 1000`. */
  positionMs: number;
  /** Milliseconds of the whole chapter. Zero or non-finite renders an inert rail, never NaN%. */
  durationMs: number;
  /**
   * An explicit seek, in chapter milliseconds.
   *
   * ⚠️ The caller must resolve this to a **sentence** before it touches audio — LAW 3: "`ms` is
   * advisory. Resolve in-page offsets from your own cue list by `sid` + `frac`." `seekTargetInPage`
   * in `useCueSync.ts` does it for a target inside the loaded page; a target outside it is a page
   * change, which is the audio engine's business and not this component's.
   */
  onSeek(chapterMs: number): void;
  /** P5. Empty in P2. */
  ticks?: readonly ScrubberTick[];
  /** True while the chapter has no measured timeline yet — the rail is drawn, and it is inert. */
  disabled?: boolean;
}

const rail: CSSProperties = {
  position: "relative",
  flex: "1 1 auto",
  height: "var(--tap-min)",
  display: "flex",
  alignItems: "center",
  touchAction: "none", // the drag is ours; without this the browser claims it as a scroll
  cursor: "pointer",
};

export function Scrubber(props: ScrubberProps): ReactElement {
  const { positionMs, durationMs, onSeek, ticks, disabled = false } = props;

  const el = useRef<HTMLDivElement | null>(null);
  const [dragMs, setDragMs] = useState<number | null>(null);

  const usable = Number.isFinite(durationMs) && durationMs > 0;
  const shownMs = dragMs ?? (Number.isFinite(positionMs) ? positionMs : 0);
  const frac = usable ? clamp01(shownMs / durationMs) : 0;
  const live = !disabled && usable;

  const msAt = useCallback(
    (clientX: number): number => {
      const box = el.current?.getBoundingClientRect();
      if (!box || box.width <= 0) return 0;
      return clamp01((clientX - box.left) / box.width) * durationMs;
    },
    [durationMs],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!live || e.button !== 0) return;
      // Capture so a drag that leaves the rail — which it will, because the rail is 4 px tall and a
      // thumb is not — keeps sending us moves instead of being dropped at the boundary.
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragMs(msAt(e.clientX));
    },
    [live, msAt],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (dragMs === null) return;
      setDragMs(msAt(e.clientX));
    },
    [dragMs, msAt],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (dragMs === null) return;
      const target = msAt(e.clientX);
      setDragMs(null);
      onSeek(target);
    },
    [dragMs, msAt, onSeek],
  );

  const onLostCapture = useCallback(() => {
    // A cancelled gesture seeks nowhere. Committing a half-finished drag is how a phone call
    // arriving mid-scrub moves you to a random minute of the chapter.
    setDragMs(null);
  }, []);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!live) return;
      const step = e.shiftKey ? 60_000 : 15_000;
      let target: number | null = null;
      if (e.key === "ArrowRight") target = shownMs + step;
      else if (e.key === "ArrowLeft") target = shownMs - step;
      else if (e.key === "Home") target = 0;
      else if (e.key === "End") target = durationMs;
      if (target === null) return;
      // ⚠️ `preventDefault` is load-bearing, not tidiness. `usePageTurn` listens for ArrowLeft and
      // ArrowRight on `window` and bails on `e.defaultPrevented`; without this line every scrub
      // step also turns the page, and the reader ends up somewhere neither gesture asked for.
      e.preventDefault();
      onSeek(clamp01(target / durationMs) * durationMs);
    },
    [live, shownMs, durationMs, onSeek],
  );

  return (
    <div
      ref={el}
      style={{ ...rail, opacity: live ? 1 : 0.45, cursor: live ? "pointer" : "default" }}
      // A real slider, so VoiceOver's rotor offers it and a keyboard can reach it. `aria-valuetext`
      // and not the raw number: "seven hundred and forty two thousand" is not a position.
      role="slider"
      tabIndex={live ? 0 : -1}
      aria-label="Chapter position"
      aria-valuemin={0}
      aria-valuemax={usable ? Math.round(durationMs) : 0}
      aria-valuenow={Math.round(shownMs)}
      aria-valuetext={usable ? `${clock(shownMs)} of ${clock(durationMs)}` : "not timed yet"}
      aria-disabled={live ? undefined : true}
      data-no-turn=""
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onLostCapture}
      onLostPointerCapture={onLostCapture}
      onKeyDown={onKeyDown}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 3,
          borderRadius: 2,
          background: "var(--read-rule)",
        }}
      >
        <div
          style={{
            width: `${frac * 100}%`,
            height: "100%",
            borderRadius: 2,
            background: "var(--read-ink-muted)",
            // No transition. The thumb must track the finger exactly; an eased fill lags a drag and
            // reads as the control being unresponsive.
          }}
        />

        {/* P5's member ticks. Behind the thumb, so a member sitting exactly where you are does not
            hide your own position. */}
        {(ticks ?? []).map((t) =>
          usable ? (
            <span
              key={t.id}
              title={t.label}
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -2,
                left: `${clamp01(t.ms / durationMs) * 100}%`,
                width: 2,
                height: 7,
                marginLeft: -1,
                borderRadius: 1,
                background: t.color,
              }}
            />
          ) : null,
        )}

        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "50%",
            left: `${frac * 100}%`,
            width: dragMs === null ? 9 : 13,
            height: dragMs === null ? 9 : 13,
            marginLeft: dragMs === null ? -4.5 : -6.5,
            transform: "translateY(-50%)",
            borderRadius: "50%",
            background: "var(--read-ink)",
            opacity: live ? 1 : 0,
          }}
        />
      </div>
    </div>
  );
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** `h:mm:ss` past an hour, `m:ss` under it. A chapter is rarely an hour; a scripture book is. */
function clock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

export default Scrubber;
